import ContentBlock from '@components/layout/contentBlock/ContentBlock';
import ContentItem from '@components/layout/contentBlock/ContentItem';
import ContentRow from '@components/layout/contentBlock/ContentRow';
import LinkButton from '@components/button/LinkButton';
import Form from '@components/form/Form';
import AssessmentSlider from '@components/form/AssessmentSlider';
import TextArea from '@components/form/TextArea';
import ToggleSwitch from '@components/form/ToggleSwitch';
import { Assessment, Submission } from 'src/types/shared/Assessments';
import useEditAssessmentForm from './hooks/useEditAssessmentForm';
import { useHistory } from 'react-router';
import AssessmentTooltip from '../AssessmentTooltip';

interface Props {
    submission: Submission;
    assessment: Assessment;
}

const EditAssessmentForm = ({ submission, assessment }: Props) => {
    const history = useHistory();
    const redirect = () => history.push(`/assessments/submissions/submission/${submission.id}`);
    const {
        data: {
            deploymentScale,
            fitWithChallenge,
            credibilityCompany,
            credibilityTechnology,
            fullOrPartialSolution,
            isInnovative,
            finalComment,
        },
        handleChange,
        handleSubmit,
        isPatching,
        initForm,
    } = useEditAssessmentForm(assessment, redirect);

    return (
        <Form
            onSubmit={handleSubmit}
            isPosting={isPatching}
            onCancel={redirect}
            className="edit-assessment-form"
            onInit={initForm}
        >
            <ContentBlock>
                <ContentRow>
                    <LinkButton
                        href={`/assessments/submissions/submission/${submission.id}`}
                        color="green"
                        icon="search"
                    >
                        View Submission Details
                    </LinkButton>
                </ContentRow>
            </ContentBlock>

            <ContentBlock>
                <ContentRow>
                    <ContentItem
                        label={
                            <>
                                Deployment timescale (our view) <AssessmentTooltip index={0} />
                            </>
                        }
                    >
                        <AssessmentSlider
                            name="deploymentScale"
                            value={deploymentScale}
                            onChange={handleChange}
                        />
                    </ContentItem>
                </ContentRow>

                <ContentRow>
                    <ContentItem
                        label={
                            <>
                                Fit with challenge (cost / benefit) <AssessmentTooltip index={1} />
                            </>
                        }
                    >
                        <AssessmentSlider
                            name="fitWithChallenge"
                            value={fitWithChallenge}
                            onChange={handleChange}
                        />
                    </ContentItem>
                </ContentRow>

                <ContentRow>
                    <ContentItem
                        label={
                            <>
                                Credibility of Company <AssessmentTooltip index={2} />
                            </>
                        }
                    >
                        <AssessmentSlider
                            name="credibilityCompany"
                            value={credibilityCompany}
                            onChange={handleChange}
                        />
                    </ContentItem>
                </ContentRow>

                <ContentRow>
                    <ContentItem
                        label={
                            <>
                                Credibility of Technology <AssessmentTooltip index={3} />
                            </>
                        }
                    >
                        <AssessmentSlider
                            name="credibilityTechnology"
                            value={credibilityTechnology}
                            onChange={handleChange}
                        />
                    </ContentItem>
                </ContentRow>

                <ContentRow>
                    <ContentItem
                        label={
                            <>
                                Full or partial solution to specification of innovation challenge{' '}
                                <AssessmentTooltip index={4} />
                            </>
                        }
                    >
                        <AssessmentSlider
                            name="fullOrPartialSolution"
                            value={fullOrPartialSolution}
                            onChange={handleChange}
                        />
                    </ContentItem>
                </ContentRow>

                <ContentRow>
                    <ContentItem label="Is it Innovative?">
                        <ToggleSwitch
                            name="isInnovative"
                            value={isInnovative}
                            onChange={handleChange}
                        />
                    </ContentItem>
                </ContentRow>

                <ContentRow>
                    <ContentItem>
                        <TextArea
                            name="finalComment"
                            value={finalComment}
                            onChange={handleChange}
                            style={{ width: '100%', marginTop: 8 }}
                            label="Final Comment"
                            minRows={5}
                            required
                        />
                    </ContentItem>
                </ContentRow>
            </ContentBlock>
        </Form>
    );
};

export default EditAssessmentForm;
